import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import each from 'lodash/each';
import flatten from 'lodash/flatten';
import map from 'lodash/map';
import omit from 'lodash/omit';
import filter from 'lodash/filter';
import get from 'lodash/get';

// A node in the target/dashboard tree. Loose by design — these helpers operate on
// server-shaped data whose full shape we don't model here.
interface TreeTarget {
  _id: string;
  toggled?: boolean;
  active?: boolean;
  children?: TreeTarget[];
  [key: string]: unknown;
}

/**
 *  Restores tree component state (active nodes, toggled nodes) and removes empty childrens.
 *  @param  {Object} target  Target
 */
const prepareTreeState = (target: TreeTarget, toggled: string[] = [], selectedNodeId: string | null = null): void => {
  // Restore toggled nodes.
  if (toggled.length > 0 && toggled.includes(target._id)) {
    target.toggled = true;
  }

  // Restore active node.
  if (selectedNodeId && selectedNodeId === target._id) {
    target.active = true;
  }

  // Remove empty children.
  if (isEmpty(target.children)) {
    delete target.children;
  } else {
    each(target.children, (children) => prepareTreeState(children, toggled, selectedNodeId));
  }
};

/**
 *  [description]
 *  @param  {[type]} data [description]
 *  @return {[type]}      [description]
 */
const filterDisabledTargets = (target: TreeTarget): TreeTarget | void => {
  if (isEmpty(target.children)) {
    return target;
  }

  target.children = filter(target.children, child => get(child, 'extra.c.enabled', true) === true);

  each(target.children, (children) => filterDisabledTargets(children));
};

/**
 *  Gets node ID alongs with all of its children IDs.
 *  @param  {Object} node Node
 *  @return {Array}       IDs
 */
const getChildren = (node: TreeTarget): string | string[] => {
  if (isArray(node.children)) {
    return [node._id].concat(
      flatten(
        map(node.children, getChildren)
      )
    );
  } else {
    return node._id;
  }
};

/**
 *  [Omit values we do not need to save and adjust tags to an array.]
 *  @param  {[object]} target [All target fields]
 *  @return {[object]}        [Trimmed target for the API]
 */
const prepareTargetForAPI = (target: Record<string, any>): Record<string, any> => {
  // Adjust tags to an array and remove whitespaces.
  /*
  if (target.tags && typeof target.tags.split === 'function') {
    target.tags = target.tags.split(",").map(item => item.trim());
    console.log(JSON.stringify(target.tags))
  };
  */
  return omit(target, [
    '__v',
    '_id',
    'active',
    'children',
    'createdAt',
    'customImage',
    'customImagePreview',
    'entityType',
    'path',
    'toggled',
    'updatedAt',
    'theme',
    'qrcodetitle',
    'qrcodeurl',
    'qrcodescale',
    'qrcodeid',
  ]);
};

const prepareDashboardForAPI = (dashboard: Record<string, any>): Record<string, any> => {
  return omit(dashboard, [
    '__v',
    '_id',
    'createdAt',
    'entityType',
    'updatedAt'
  ]);
};

//--------------------------------------------------------------------
// Get target id from linking URL
//--------------------------------------------------------------------
const getTargetId = (URL: string): string => {
  if (typeof URL === 'string' && URL.length > 10) {
    let targetId = URL.replace(/https:\/\/go.taplause.com\//,'').replace(/nps\/|fiilis\/|generic\/|plus\//,'').split('?')[0];
    if (targetId.split('/').length) {
      targetId = targetId.split('/')[targetId.split('/').length - 1]
    };
    // console.log('++++ URL;%O, targetId:%O', URL, targetId);
    return targetId;
  } else {
    return '';
  };
};

// What the two walkers below need of a node: an id and possibly children.
// Looser than TreeTarget, whose `_id` is required — the store's tree starts as
// `{}` and its nodes are typed with `_id` optional.
interface TreeWalkNode {
  _id?: string;
  children?: TreeWalkNode[];
  [key: string]: unknown;
}

/**
 *  Finds the key path from the tree root down to the node with `nodeId`.
 *
 *  Replaces `findKey(flattenObject(tree), v => v === nodeId)`, which
 *  materialised the ENTIRE tree as a flat map — one entry per leaf property of
 *  every node, so a few hundred targets produced tens of thousands of keys — and
 *  then scanned it, just to locate one node. `selectNode` did that three times
 *  per click (deselectNode, toggleNode, then parseTargets), which is what made
 *  selecting a node slow on large trees.
 *
 *  This walks `children` directly and stops at the first match. It also matches
 *  on `_id` specifically, where the old scan compared every property value and
 *  could in principle have matched some other field holding the same id.
 *
 *  @param  node   Tree root (or any subtree).
 *  @param  nodeId The `_id` to find.
 *  @return Path segments from `node` to the match, e.g.
 *          `['children', '0', 'children', '2']`, or null if not found. An empty
 *          array means `node` itself is the match.
 */
const findNodeKeyPath = (node: TreeWalkNode | undefined, nodeId: string): string[] | null => {
  if (!node || typeof node !== 'object') {
    return null;
  }
  if (node._id === nodeId) {
    return [];
  }
  const children = node.children;
  if (Array.isArray(children)) {
    for (let i = 0; i < children.length; i++) {
      const found = findNodeKeyPath(children[i], nodeId);
      if (found) {
        return ['children', String(i), ...found];
      }
    }
  }
  return null;
};

/**
 *  Collects every node of the tree, depth-first, in the same order the old
 *  `flattenObject` + `pickBy(endsWith('_id'))` scan produced.
 *
 *  @param  node Tree root (or any subtree).
 *  @return Every node including the root.
 */
const collectTreeNodes = (node: TreeWalkNode | undefined): TreeWalkNode[] => {
  if (!node || typeof node !== 'object') {
    return [];
  }
  const nodes: TreeWalkNode[] = [node];
  if (Array.isArray(node.children)) {
    for (const child of node.children) {
      nodes.push(...collectTreeNodes(child));
    }
  }
  return nodes;
};

export {
  findNodeKeyPath,
  collectTreeNodes,
  prepareTreeState,
  filterDisabledTargets,
  getChildren,
  prepareTargetForAPI,
  prepareDashboardForAPI,
  getTargetId,
}
